import { useQuery } from '@tanstack/react-query'
import { type SelectLocationFull, selectLocationsFullSchema } from '@workspace/db/extra'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'

export interface LocationRow {
  code: string
  name: string
  status: 'Active' | 'Inactive'
}

// Local envelope mirroring GET /v1/locations ({ data, success, code, errors }).
// Deliberately not imported from the @workspace/core barrel (server-only deps).
const locationsEnvelopeSchema = z.object({
  data: selectLocationsFullSchema,
  success: z.boolean(),
  code: z.union([z.string(), z.number()]).optional(),
  errors: z.unknown().optional(),
})

export function toRows(data: SelectLocationFull[]): LocationRow[] {
  return data.map((l) => ({
    code: l.aptosStoreCode ?? '',
    name: l.name ?? '',
    status: l.status ? 'Active' : 'Inactive',
  }))
}

export function useLocations(banner = 'dtlr') {
  const api = useApi()
  // Preview mode (public /preview route): serve mock rows immediately, never touch the BFF.
  // Hooks stay unconditional; `enabled: false` + `initialData` yields data with no fetch/error.
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'locations'] : ['locations', banner],
    queryFn: async (): Promise<LocationRow[]> => {
      const res = await api.get(
        `/v1/locations?banner=${encodeURIComponent(banner)}&activeOnly=true&withOrganization=true`,
        locationsEnvelopeSchema,
      )
      if (!res.success) throw new Error(`Failed to fetch locations (code ${res.code ?? 'unknown'})`)
      return toRows(res.data)
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.locations, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}
